/**
 * `weather.v1` — wire → canonical decoder for Weather-shaped cells.
 *
 * The generated `SheetsNetwork.WeatherCell` model hydrates the live
 * day-level weather shape. The older scalar test fixture shape
 * still falls back to local parsing.
 *
 * Canonical shape for live `weather@lona.so` rows is the server's
 * day-level `WeatherData` payload:
 *   `{ date, day, astro, hours, source, storedAt }`
 *
 * The older scalar `{ temperature, observedAt }` shape is still accepted for
 * test fixtures that predate the weather row migration.
 */

import { DateTime, type Utc } from "@tento-chrono";
import { Field, Model, Scalar } from "@tento-core/model";

import type { CellValue, Weather } from "../core/dtype.ts";
import type { EncodingDecoder } from "../core/encoding-registry.ts";
import { Json } from "../core/json.ts";
import { SheetsNetwork } from "../wire/rows-network.ts";
import type { Option } from "../core/option.ts";

/** The `weather.v1` wire-encoding name a weather source row declares in its metadata. */
export const WEATHER_V1 = "weather.v1";

/** An RFC 3339 wire string as a UTC `DateTime`. */
const UTC_TIME: Model<DateTime<Utc>> = Scalar.checked<string, DateTime<Utc>>(
  "DateTime<Utc>",
  Scalar.string,
  (raw) => {
    const parsed = DateTime.fromRfc3339(raw);
    return parsed.isErr ? null : parsed.exp().toUtc();
  },
  null,
);

/**
 * The fixture-era scalar shape: a numeric `temperature`, an RFC 3339
 * `observedAt`, and an optional numeric `precipitation` (any other value
 * there is ignored, as it always was).
 */
const SCALAR_WEATHER = Model.object<{ temperature: number; observedAt: DateTime<Utc> }>({
  temperature: Field.required(Scalar.number),
  observedAt: Field.required(UTC_TIME),
});

/** Decodes a `weather.v1` wire cell to a canonical weather reading (`null`: not one). */
export const weatherV1: EncodingDecoder = (wire): Option<Json | CellValue> => {
  const raw = Json.object(Json.wire(wire));
  if (raw == null) return null;

  if (Json.child(raw, "day") != null) {
    const result = SheetsNetwork.WeatherCell.safeHydrate(raw);
    return result.isOk ? raw : null;
  }

  const result = SCALAR_WEATHER.safeHydrate(raw);
  if (!result.isOk) return null;
  const precipitation = Json.number(raw, "precipitation");
  const weather: Weather = {
    temperature: result.value.temperature,
    observedAt: result.value.observedAt,
    precipitation,
  };
  return weather;
};
